<script setup lang="ts">
import AnimateCategoryChapterDone from '@jaaluu/components/AnimateCategoryChapterDone.vue';
import AnimateCourseChapterDone from '@jaaluu/components/AnimateCourseChapterDone.vue';
import AnimateGrammarDone from '@jaaluu/components/AnimateGrammarDone.vue';
import AnimationLoading from '@jaaluu/components/AnimationLoading.vue';
import CookieConsent from '@jaaluu/components/CookieConsent.vue';
import Snackbar from '@shared/components/Snackbar.vue';
import i18n from '@shared/i18n';
import {
  ConfigService,
  GoogleRssTopic,
  Language,
  LanguageLevel,
  VideoDef
} from '@shared/openapi/jaaluu-api';
import { client } from '@shared/openapi/jaaluu-api/services.gen';
import sharedStores from '@shared/shared-stores';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AnimateSynapsisScore from './components/AnimateSynapsisScore.vue';
import AnimateWordMoved from './components/AnimateWordMoved.vue';
import AppBar from './components/AppBar.vue';
import Footer from './components/Footer.vue';
import SynapsisScore from './components/SynapsisScore.vue';
import Tooltips from './components/Tooltips.vue';
import TourGuide from './components/TourGuide.vue';
import stores from './stores';
import { getLangSubdomain } from '@jaaluu/utils/subdomain';

const BASE_URL_API = (import.meta as any).env.VITE_JAALUU_API_URL;
const BASE_URL_APP = (import.meta as any).env.VITE_JAALUU_URL;

const langDomain = getLangSubdomain();
if (langDomain) {
  client.setConfig({
    baseURL: BASE_URL_API.replace('://', `://${langDomain}.`)
  });
} else {
  client.setConfig({
    baseURL: BASE_URL_API
  });
}

const { handleDefaultError } = stores.error.funcs;
const { getBrowserDefaultLanguage } = stores.language.funcs;
const { getSeenGuidesLocalStorage, tourRepeatWords, initClickOnWordAndSentence } =
  stores.tourGuide.funcs;
const { getThemeFromLocalSt, setNotifyMeFromLocalStorage } = stores.settings.funcs;
const { getIntroMsgSeenFromLs } = stores.introMessage.funcs;
const { loadStories, loadCourseStatusFromLocalStorage } = stores.course.funcs;
const { setFavorites, initLastCategoryState } = stores.favorites.funcs;
const { initCourseProgressState } = stores.courseProgress.funcs;
const { initProgressState } = stores.progress.funcs;
const { setUserData } = stores.user.funcs;
const { getCookieConsentFromLs, setCookieConsentInLs } = stores.cookies.funcs;
const { loadGrammarMenu, flattenChapters, initLastGrammarChapterState, initLastVocabularyIndexState, initLastJokeIndexState } = stores.grammar.funcs;
const { initGrammarProgressState } = stores.grammarProgress.funcs;
const { initGoogleAnalytics } = stores.gAnalytics.funcs;
const { handleKeyDown, handleKeyUp } = stores.keyControls.funcs;
const { verifyLocalStorageVersion, isFullscreenPage } = stores.app.funcs;
const { langNative, langForeign, supportedLangs, langCodeToVerbose, langCodes, langLevel } =
  stores.language.state;
const { userConsent } = stores.cookies.state;
const { ytInitDone } = stores.video.state;
const { inFullscreen } = stores.settings.state;
const { initDone } = stores.app.state;
const { count } = sharedStores.loading.state;
const { ytChannels, maxLoadVideos } = stores.video.state;
const { maxLoadFeeds } = stores.feed.state;
const { maxLoadKlexikon } = stores.klexikon.state;
const { courseStatus, showChapterDone, completedChapterNumber } = stores.course.state;
const { showCategoryChapterDone, completedSubCategory } = stores.progress.state;
const { showGrammarDone, completedGrammarChapterNumber } = stores.grammar.state;
const { activeGroupKey } = stores.favorites.state;

const router = useRouter();
const route = useRoute();

const isNotSpecialPage = computed(() => {
  return !isFullscreenPage(route);
});

const getConfig = () => {
  ConfigService.getConfigV1ConfigGet()
    .then((res) => {
      supportedLangs.value = res.data!.languages;
      langCodeToVerbose.value = res.data!.lang_code_to_verbose;
      langCodes.value = res.data!.language_codes as Record<Language, string>;
      ytChannels.value = res.data!.video_channels_def as Record<Language, VideoDef[]>;
      let langsStore: any = localStorage.getItem('languages');
      let langsCookie: any = document.cookie.match(new RegExp('(^| )' + 'languages' + '=([^;]+)'));
      langsCookie = langsCookie ? decodeURIComponent(langsCookie[2]) : null;
      const pathLangForeign = route.params.langForeign;
      console.log('Init app:');
      console.debug('Lang domain:', langDomain);
      console.debug('Path lang foreign:', pathLangForeign);
      console.debug('Langs from cookie:', langsCookie);
      console.debug('Langs from lStorage:', langsStore);
      console.debug('Is special page:', !isNotSpecialPage.value);
      if (!isNotSpecialPage.value) {
        console.debug('Current route name:', route.name);
        if (langDomain) {
          langNative.value = langDomain as Language;
          i18n.global.locale = langNative.value as any;
          document.documentElement.lang = langDomain || 'en';
        }
        if (route.name === 'welcome') {
          const storedLangsRaw = langsStore || langsCookie;
          const storedLangs = storedLangsRaw ? JSON.parse(storedLangsRaw) : null;
          if (storedLangs?.native && storedLangs?.foreign) {
            console.log('On welcome with stored langs. Redirecting.', storedLangs);
            window.location.href = `${BASE_URL_APP.replace('://', `://${storedLangs.native}.`)}/${storedLangs.foreign}`;
            return;
          }
        }
        initDone.value = true;
        return;
      }
      if (langsCookie && !langDomain) {
        langsCookie = JSON.parse(langsCookie);
        if (!langsCookie.native || !langsCookie.foreign) {
          console.log('Invalid langs cookie. Removing cookie and reloading.');
          document.cookie = `languages=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${BASE_URL_APP.split('://').slice(-1)[0]}; path=/; Secure; SameSite=None`;
          window.location.reload();
          return;
        }
        console.log('Lang from cookie set:', langsCookie);
        const baseAppUrlLangDomain = `${BASE_URL_APP.replace('://', `://${langsCookie.native}.`)}/${langsCookie.foreign}`;
        window.location.href = baseAppUrlLangDomain;
      } else if (!langDomain) {
        if (langsStore) {
          langsStore = JSON.parse(langsStore);
          console.log('No lang domain. Lang from lStorage', langsStore);
          const baseAppUrlLangDomain =
            BASE_URL_APP.replace('://', `://${langsStore.native}.`) + `/${langsStore.foreign}`;
          window.location.href = baseAppUrlLangDomain;
        } else if (!langsStore) {
          console.log('No lang domain. No langsf from lStorage. Redircting.');
          const langDefault = getBrowserDefaultLanguage();
          const baseAppUrlLangDomain =
            BASE_URL_APP.replace('://', `://${langDefault}.`) + `/welcome`;
          window.location.href = baseAppUrlLangDomain;
        }
      } else if (isNotSpecialPage.value && pathLangForeign) {
        langNative.value = langDomain.toString() as Language;
        langForeign.value = pathLangForeign.toString() as Language;
        i18n.global.locale = langNative.value as any;
        document.documentElement.lang = langDomain || 'en';
        getLoadMax();
        initClickOnWordAndSentence();
      } else if (!pathLangForeign && route.path.startsWith('/welcome')) {
        langNative.value = langDomain.toString() as Language;
        i18n.global.locale = langNative.value as any;
        document.documentElement.lang = langDomain || 'en';
      } else if (!pathLangForeign && isNotSpecialPage && langsStore) {
        langsStore = JSON.parse(langsStore);
        const baseAppUrlLangDomain = `${BASE_URL_APP.replace('://', `://${langsStore.native}.`)}/${langsStore.foreign}`;
        window.location.href = baseAppUrlLangDomain;
      } else if (!pathLangForeign && isNotSpecialPage) {
        const langDomain = getLangSubdomain();
        langNative.value = langDomain as Language;
        langNative.value = Language.EN;
        i18n.global.locale = langNative.value as any;
        document.documentElement.lang = langDomain || 'en';
        setTimeout(() => {
          router.push('/welcome');
        }, 500);
      } else {
        console.error('Init detection failed');
      }
      let langLevelStore: any = localStorage.getItem('langLevel');
      if (langLevelStore) {
        langLevel.value = langLevelStore;
      } else {
        langLevel.value = LanguageLevel.A1_A2;
      }
      if (langLevel.value === LanguageLevel.A1_A2) {
        activeGroupKey.value = 'course';
      }
      setTimeout(() => {
        setFavorites();
        initCourseStates();
      }, 500);
    })
    .catch(handleDefaultError);
};

const getCookies = () => {
  const marketing = route.query.c_m;
  const preferences = route.query.c_p;
  const statistics = route.query.c_s;
  console.log('marketing', marketing);
  if (marketing && preferences && statistics) {
    userConsent.value.marketing = !!marketing;
    userConsent.value.preferences = !!preferences;
    userConsent.value.statistics = !!statistics;
    setCookieConsentInLs();
  } else {
    getCookieConsentFromLs();
  }
};

const initYoutubeApi = () => {
  var tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  const firstScriptTag: any = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  setTimeout(() => {
    ytInitDone.value = true;
  }, 1000);
};

onMounted(() => {
  verifyLocalStorageVersion();
  getThemeFromLocalSt();
  getConfig();
  initYoutubeApi();
  getSeenGuidesLocalStorage();
  tourRepeatWords();
  setUserData();
  initGoogleAnalytics();
  setNotifyMeFromLocalStorage();
  getIntroMsgSeenFromLs();
  getCookies();
  checkOpenInitCourse();
  setEventListener();
});

const initCourseStates = () => {
  initLastGrammarChapterState();
  initLastVocabularyIndexState();
  initLastJokeIndexState();
  loadGrammarMenu()?.then(() => {
    initGrammarProgressState(flattenChapters());
  });
  loadCourseStatusFromLocalStorage();
  initProgressState();
  initLastCategoryState();
  loadStories().then(() => {
    initCourseProgressState().then(() => {
      console.log('Course progress state initialized');
      initDone.value = true;
    });
  });
};

const setEventListener = () => {
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
};

const checkOpenInitCourse = () => {
  const initCourse = route.query['init-course'];
  if (initCourse === '1') {
    setTimeout(() => {
      courseStatus.value.isActive = true;
      courseStatus.value.updateIsActive = false;
      router.push({ name: 'course-status', params: { langForeign: langForeign.value } });
    }, 300);
  }
};

const getLoadMax = () => {
  if (!langForeign.value || !langLevel.value) return;
  ConfigService.getLoadCountsV1ConfigLoadCountsGet({
    query: { lang: langForeign.value, lang_level: langLevel.value }
  })
    .then((res) => {
      maxLoadVideos.value = res.data!.videos;
      maxLoadFeeds.value = res.data!.feeds as Record<GoogleRssTopic, number>;
      maxLoadKlexikon.value = res.data!.klexikon;
      initCourseStates();
    })
    .catch(handleDefaultError);
};

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('keyup', handleKeyUp);
});

watch(
  () => langForeign.value,
  () => getLoadMax()
);

watch(
  () => langLevel.value,
  (newLevel, oldLevel) => {
    if (newLevel && oldLevel && newLevel !== oldLevel) {
      loadStories().then(() => {
        initCourseProgressState();
      });
    }
    getLoadMax();
  }
);
</script>

<template>
  <v-app>
    <v-icon color="error" icon="mdi-emoticon-sad-outline" id="sad-face" />
    <v-progress-linear
      v-if="count > 0"
      indeterminate
      color="green"
      class="global-progress-linear"
    ></v-progress-linear>
    <Snackbar />
    <AppBar v-if="!isFullscreenPage(route)" />
    <TourGuide />
    <CookieConsent />
    <AnimationLoading v-if="!initDone && !langDomain" />
    <div class="">
      <div
        class="flex-grow min-height"
        :class="{
          'pt-2 pa-sm-2 pa-md-4 pa-xl-8 max-width': !inFullscreen && !isFullscreenPage(route)
        }"
      >
        <v-fade-transition>
          <v-main class="main-wrapper" v-show="initDone">
            <div fluid >
              <router-view />
              <SynapsisScore />
            </div>
          </v-main>
        </v-fade-transition>
      </div>
      <Footer :class="{ 'mt-12': !isFullscreenPage }" />
    </div>
    <Tooltips />
    <AnimateWordMoved />
    <AnimateSynapsisScore />
    <AnimateCourseChapterDone :show="showChapterDone" :chapter-number="completedChapterNumber" />
    <AnimateCategoryChapterDone :show="showCategoryChapterDone" :sub-category="completedSubCategory" @close="showCategoryChapterDone = false" />
    <AnimateGrammarDone :show="showGrammarDone" :chapter-number="completedGrammarChapterNumber" />
  </v-app>
</template>

<style scopes lang="scss">
#sad-face {
  position: fixed;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 5rem;
  z-index: 9999;
  display: none;
}
.min-height {
  min-height: 100vh;
}
#full-imgx {
  max-width: 40%;
  margin: 0 auto;
  background-position-x: center;
  background-position-y: center;
  background-size: cover;
}
</style>
